import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from '@/lib/api';
import { X, Check, Bell, Users, Map, Mail, Settings, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getTranslatedValue } from "@/components/TranslatableField";
import { BACKEND_URL } from "../config";
// Icon mapping for notification contexts
const contextIcons = {
  ctx_grupos: Users,
  ctx_passeios: Map,
  ctx_convites: Mail,
  ctx_sistema: Settings,
  default: Bell,
};

// Single toast card component
function NotificationToastCard({ notification, onDismiss, onMarkAsRead }) {
  const { t, i18n } = useTranslation("notifications");
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);
  const currentLang = i18n.language?.split("-")[0] || "pt";
  
  const IconComponent = contextIcons[notification.context_id] || contextIcons.default;
  
  // Helper to safely extract string from translatable field or plain string
  const extractString = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      return getTranslatedValue(field, currentLang) || "";
    }
    return String(field);
  };
  
  // Translate notification title/message based on type_code and data
  const getTranslatedNotification = () => {
    const typeCode = notification.type_code;
    const data = notification.data || {};
    
    const titleKey = `types.${typeCode}.title`;
    const messageKey = `types.${typeCode}.message`;
    
    const translatedTitle = t(titleKey, data);
    const translatedMessage = t(messageKey, data);
    
    const hasTranslation = translatedTitle !== titleKey;
    
    return {
      title: hasTranslation ? translatedTitle : extractString(notification.title),
      message: hasTranslation ? translatedMessage : extractString(notification.message)
    };
  };
  
  const { title, message } = getTranslatedNotification();
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return t("time.now", "Agora");
      if (diffMins < 60) return t("time.mins_ago", { count: diffMins, defaultValue: `Há ${diffMins} min` });
      
      const hours = date.getHours().toString().padStart(2, '0');
      const mins = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${mins}`;
    } catch {
      return "";
    }
  };
  
  // Handle exit animation
  const handleExit = useCallback((callback) => {
    setIsExiting(true);
    setTimeout(() => {
      callback();
    }, 300); // Match animation duration
  }, []);
  
  // Handle dismiss (close without marking as read)
  const handleDismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    handleExit(() => onDismiss(notification.notification_id));
  };
  
  // Handle mark as read
  const handleMarkAsRead = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      await api.post(
        `/api/notifications/${notification.notification_id}/read`,
        {}
      );
      handleExit(() => onMarkAsRead(notification.notification_id));
    } catch (error) {
      console.error("Error marking notification as read:", error);
      handleExit(() => onDismiss(notification.notification_id));
    }
  };
  
  // Handle link click
  const isExternalLink = notification.link_url?.startsWith("http://") || notification.link_url?.startsWith("https://");

  const handleLinkClick = () => {
    if (notification.link_url) {
      handleMarkAsRead();
      if (isExternalLink) {
        window.open(notification.link_url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(notification.link_url);
      }
    }
  };
  
  // Auto-dismiss after 10 seconds
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      handleExit(() => onDismiss(notification.notification_id));
    }, 10000);
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [notification.notification_id, onDismiss, handleExit]);
  
  return (
    <div
      className={cn(
        "w-80 bg-card border border-border rounded-xl shadow-2xl overflow-hidden",
        "transform transition-all duration-300 ease-out",
        isExiting 
          ? "translate-x-full opacity-0" 
          : "translate-x-0 opacity-100 animate-slide-in-right"
      )}
      data-testid={`notification-toast-${notification.notification_id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-primary/10 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-full bg-primary/20">
            <IconComponent className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-xs font-medium text-muted-foreground">
            {formatDate(notification.created_at)}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 hover:bg-destructive/20"
          onClick={handleDismiss}
          data-testid="toast-close-btn"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-2">
        <h4 className="font-semibold text-sm text-foreground">
          {title}
        </h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {message}
        </p>
        
        {/* Context info (club name, etc.) */}
        {notification.context_name && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
            <IconComponent className="h-3 w-3" />
            <span>{extractString(notification.context_name)}</span>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs gap-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
          onClick={handleMarkAsRead}
          data-testid="toast-mark-read-btn"
        >
          <Check className="h-3.5 w-3.5" />
          {t("actions.mark_as_read")}
        </Button>
        
        {notification.link_url && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={handleLinkClick}
            data-testid="toast-link-btn"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t("toast.view", "Ver")}
          </Button>
        )}
      </div>
      
      {/* Progress bar for auto-dismiss */}
      <div className="h-1 bg-muted overflow-hidden">
        <div 
          className="h-full bg-primary/50 animate-shrink-width"
          style={{ animationDuration: '10s' }}
        />
      </div>
    </div>
  );
}

// Container that manages multiple toasts
export function NotificationToastContainer() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [knownIds, setKnownIds] = useState(new Set());
  const isInitialized = useRef(false);
  
  // Fetch latest notifications and show new ones as toasts
  const fetchAndShowNewNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const res = await api.get("/api/notifications?limit=5");
      const notifications = res.data.notifications || [];
      
      // Filter to only unread notifications
      const unread = notifications.filter(n => !n.is_read);
      
      // On first fetch, just record IDs without showing toasts
      if (!isInitialized.current) {
        isInitialized.current = true;
        setKnownIds(new Set(unread.map(n => n.notification_id)));
        return;
      }
      
      // Find truly new notifications
      const newNotifications = unread.filter(n => !knownIds.has(n.notification_id));
      
      // Update known IDs
      setKnownIds(new Set(unread.map(n => n.notification_id)));
      
      // Show new notifications as toasts
      if (newNotifications.length > 0) {
        setToasts(prev => [...prev, ...newNotifications]);
      }
    } catch (error) {
      // Silently fail
    }
  }, [isAuthenticated, knownIds]);
  
  // Initialize on auth ready
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    
    // Delay initial fetch to let other components load first
    const timeout = setTimeout(fetchAndShowNewNotifications, 3000);
    return () => clearTimeout(timeout);
  }, [authLoading, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Listen for new notifications event from NotificationBell
  useEffect(() => {
    const handleNewNotifications = () => {
      // Small delay to ensure backend has processed
      setTimeout(fetchAndShowNewNotifications, 500);
    };
    
    window.addEventListener('newNotificationsAvailable', handleNewNotifications);
    return () => window.removeEventListener('newNotificationsAvailable', handleNewNotifications);
  }, [fetchAndShowNewNotifications]);
  
  // Remove toast from list
  const removeToast = useCallback((notificationId) => {
    setToasts(prev => prev.filter(t => t.notification_id !== notificationId));
  }, []);
  
  // Handle mark as read - also dispatch event to update bell
  const handleMarkAsRead = useCallback((notificationId) => {
    removeToast(notificationId);
    // Dispatch event to update notification bell count
    window.dispatchEvent(new CustomEvent('notificationRead'));
  }, [removeToast]);
  
  // Don't render if no toasts
  if (toasts.length === 0) return null;
  
  return (
    <div 
      className="fixed bottom-24 right-4 z-50 flex flex-col-reverse gap-3"
      data-testid="notification-toast-container"
    >
      {toasts.map((notification) => (
        <NotificationToastCard
          key={notification.notification_id}
          notification={notification}
          onDismiss={removeToast}
          onMarkAsRead={handleMarkAsRead}
        />
      ))}
    </div>
  );
}

export default NotificationToastContainer;
